import classNames from "classnames";
import { DateTime } from "luxon";
import { useState } from "react";
import { RRule } from "rrule";
import {
	createRRuleSet,
	getNextNOccurrences,
	getPreviousNOccurrences,
	RDateDescriptor,
	rruleTimeToLocalTime,
} from "../../../../../../common/repeat_utils";
import { time_format_utils } from "../../../../../../common/time_format_utils";
import { DashboardUtils } from "../../../dashboard_utils";
import { ModifySettingsButton } from "../base_components/modify_settings_button";
import { SeshPrimaryButton } from "../base_components/sesh_primary_button";
import {
	DropdownItem,
	WithDropdownImproved,
} from "../base_components/with_dropdown_improved";
import { BoxItem } from "../box_item";
import { EditEventTimeContent } from "../hooks/edit_event_time_content";
import { useEventCreationContext } from "../hooks/use_event_creation_context";

function createRDatesState(
	rdates: RDateDescriptor[],
	time_being_edited: Date,
	date: Date
) {
	const result_rdates = [...(rdates ?? [])].filter(
		(val) => new Date(val.rdate).getTime() !== time_being_edited.getTime()
	);

	if (time_being_edited.getTime() !== date.getTime()) {
		const orig_date =
			rdates?.find(
				(val) =>
					new Date(val.rdate).getTime() ===
					time_being_edited.getTime()
			)?.orig_date ?? time_being_edited;

		result_rdates.push({
			orig_date,
			rdate: date,
		});
	}

	return result_rdates;
}

function getOriginalOccurrenceDate(
	rdates: RDateDescriptor[],
	time_to_find: Date
) {
	return (
		rdates?.find(
			(val) => new Date(val.rdate).getTime() === time_to_find.getTime()
		)?.orig_date ?? time_to_find
	);
}

export function EditRepeatOccurrencesSection(props: {}) {
	const [is_open, setIsOpen] = useState(false);
	const { state } = useEventCreationContext();

	return (
		<>
			<div className="flex flex-col">
				<ModifySettingsButton
					hide_arrow
					onClick={() => setIsOpen(true)}
					disabled={!state.start_time}
				>
					Edit Repeat Occurrences
				</ModifySettingsButton>
			</div>
			{is_open && (
				<EditRepeatOccurrencesModal onClose={() => setIsOpen(false)} />
			)}
		</>
	);
}
function EditRepeatOccurrencesModal({ onClose }: { onClose: () => void }) {
	return (
		<DashboardUtils.SeshModal
			title={"Repeat Occurrences"}
			is_active
			onClose={onClose}
			content={
				<div className="mt-2">
					<EditRepeatOccurrencesModalContent onClose={onClose} />
				</div>
			}
			allow_overflow
		/>
	);
}
function EditRepeatOccurrencesModalContent({
	onClose,
}: {
	onClose: () => void;
}) {
	const { state, setState } = useEventCreationContext();

	const [time_currently_being_edited, setTimeCurrentlyBeingEdited] = useState(
		null as Date | null
	);

	// TODO: input different thing for state time if part of an rdate
	if (time_currently_being_edited) {
		const original_date = getOriginalOccurrenceDate(
			state.rdates,
			state.start_time.toJSDate()
		);

		return (
			<div className="flex flex-col gap-4">
				<EditEventTimeContent
					rrule={RRule.optionsToString(state.rrule.origOptions)}
					repeat_start_time={state.start_time.toJSDate()}
					original_instance_start_time={new Date(original_date)}
					start_time={time_currently_being_edited}
					end_time={DateTime.fromJSDate(time_currently_being_edited)
						.plus({
							milliseconds: state.duration_ms ?? 1000 * 60 * 60,
						})
						.toJSDate()}
					repeat_tz={state.event_timezone}
					exdates={
						state.exdates?.map((val) => val.toISOString()) ?? []
					}
					rdates={state.rdates}
					onSubmit={(date) => {
						setState((state) => {
							return {
								...state,
								rdates: createRDatesState(
									state.rdates,
									time_currently_being_edited,
									date
								),
							};
						});
						setTimeCurrentlyBeingEdited(null);
					}}
					onBack={() => setTimeCurrentlyBeingEdited(null)}
				/>
			</div>
		);
	}

	return (
		<RepeatOccurrencesList
			onSubmit={onClose}
			onEditTimeSelected={(time) => setTimeCurrentlyBeingEdited(time)}
		/>
	);
}
function RepeatOccurrencesList({
	onSubmit,
	onEditTimeSelected,
}: {
	onSubmit?: () => void;
	onEditTimeSelected: (time: Date) => void;
}) {
	const { state, setState } = useEventCreationContext();

	const rrule_str = RRule.optionsToString(
		time_format_utils.transformRRuleToTimezone(
			state.rrule.origOptions,
			state.user_timezone,
			state.start_time
		)
	);

	const rrule_set = createRRuleSet({
		rrule_str,
		start_time_js: state.start_time.toJSDate(),
		repeat_tz: state.event_timezone,
		rdates: [],
	});

	const [dates_to_list, setDatesToList] = useState(
		getNextNOccurrences(
			rrule_set,
			state.start_time.toJSDate(),
			state.event_timezone,
			10,
			true
		)
	);

	const exdates_set = new Set(
		state.exdates?.map((val) => val.getTime()) ?? []
	);

	const dates = dates_to_list.map((val) => {
		const existing_rdate = state.rdates?.find(
			(rdate) =>
				new Date(rdate.orig_date).getTime() ===
				rruleTimeToLocalTime(val).toMillis()
		);

		return existing_rdate
			? DateTime.fromJSDate(new Date(existing_rdate.rdate))
			: rruleTimeToLocalTime(val);
	});

	const can_go_back =
		getPreviousNOccurrences(
			rrule_set,
			state.start_time.toJSDate(),
			dates[0].toJSDate(),
			state.event_timezone,
			10
		).length > 0;
	const can_go_forward =
		getNextNOccurrences(
			rrule_set,
			dates[dates.length - 1].toJSDate(),
			state.event_timezone,
			1,
			false
		).length > 0;

	return (
		<div>
			<div className="ml-auto flex w-fit">
				<SeshPrimaryButton
					disabled={!can_go_back}
					className="px-4 font-bold text-sesh-primary hover:text-sesh-dark"
					custom_size="small"
					onClick={(e) => {
						e.preventDefault();

						if (!can_go_back) return;

						setDatesToList(
							getPreviousNOccurrences(
								rrule_set,
								state.start_time.toJSDate(),
								dates[0].toJSDate(),
								state.event_timezone,
								10
							)
						);
					}}
				>
					<i className="fas fa-chevron-left"></i>
				</SeshPrimaryButton>
				<SeshPrimaryButton
					custom_size="small"
					disabled={!can_go_forward}
					className="px-4 font-bold text-sesh-primary hover:text-sesh-dark"
					onClick={(e) => {
						e.preventDefault();

						if (!can_go_forward) return;

						setDatesToList(
							getNextNOccurrences(
								rrule_set,
								dates[dates.length - 1].toJSDate(),
								state.event_timezone,
								10,
								false
							)
						);
					}}
				>
					<i className="fas fa-chevron-right"></i>
				</SeshPrimaryButton>
			</div>
			<div className="flex flex-col gap-1">
				{dates.map((val) => (
					<RepeatOccurrenceItem
						key={val.toISO()}
						is_already_excluded={exdates_set.has(val.toMillis())}
						date={val}
						onEditTimeSelected={() =>
							// TODO: put in original time probably (not rdate time)
							onEditTimeSelected(val.toJSDate())
						}
					/>
				))}
			</div>

			<div className="mt-2 flex">
				<div className="ml-auto flex gap-4">
					<div className="w-fit">
						<SeshPrimaryButton
							onClick={() => {
								onSubmit();
							}}
						>
							Close
						</SeshPrimaryButton>
					</div>
				</div>
			</div>
		</div>
	);
}
function RepeatOccurrenceItem({
	date,
	onEditTimeSelected,
	is_already_excluded,
}: {
	date: DateTime;
	is_already_excluded: boolean;
	onEditTimeSelected: () => void;
}) {
	const { setState } = useEventCreationContext();

	const delete_or_restore_item: DropdownItem = is_already_excluded
		? {
				label: "Restore",
				type: "button",
				icon: <i key={"restore"} className="fas fa-redo-alt"></i>,
				props: {
					onClick: (e) => {
						e.preventDefault();
						setState((state) => {
							const exdates = state.exdates ?? [];
							exdates.splice(
								exdates.findIndex(
									(val) => val.getTime() === date.toMillis()
								),
								1
							);
							return {
								...state,
								exdates: exdates,
							};
						});
					},
				},
		  }
		: {
				label: "Delete",
				icon: <i key={"delete"} className="fas fa-trash" />,
				type: "button",
				is_warning: true,
				props: {
					onClick: (e) => {
						e.preventDefault();
						setState((state) => {
							const exdates = state.exdates ?? [];
							exdates.push(date.toJSDate());

							const unique_exdates = [
								...new Set(exdates.map((val) => val.getTime())),
							]
								.map((ms) => new Date(ms))
								.sort((a, b) => a.getTime() - b.getTime());

							return {
								...state,
								exdates: unique_exdates,
							};
						});
					},
				},
		  };

	return (
		<BoxItem
			key={is_already_excluded.toString()}
			bgColor={"semidark"}
			className={classNames(
				"group relative flex items-center gap-2 !p-2",
				{
					"opacity-50": is_already_excluded,
				}
			)}
			disabled={is_already_excluded}
		>
			<div
				className={classNames("text-sesh-primary", {
					"text-sesh-primary": !is_already_excluded,
					"text-warning": is_already_excluded,
				})}
			>
				<i className="fas fa-calendar-day" />
			</div>
			<div className="font-mono">
				{`${date.toLocaleString({
					weekday: "short",
					month: "short",
					day: "numeric",
					hour: "numeric",
					minute: "numeric",
					year:
						date.year !== DateTime.now().year
							? "numeric"
							: undefined,
				})} ${DateTime.local().zone.offsetName(new Date().getTime(), {
					format: "short",
				})}`}
			</div>
			<div className="ml-auto flex w-fit items-center gap-2">
				{is_already_excluded && (
					<div className="text-xs font-semibold text-warning">
						Canceled
					</div>
				)}
				<WithDropdownImproved
					alignment="right"
					items={[
						{
							label: "Edit time",
							icon: <i className="fas fa-clock" />,
							type: "button",
							props: {
								onClick: (e) => {
									e.preventDefault();
									onEditTimeSelected();
								},
							},
						},
						delete_or_restore_item,
					]}
				>
					<i className="fas fa-ellipsis-h opacity-50 group-hover:opacity-100"></i>
				</WithDropdownImproved>
			</div>
		</BoxItem>
	);
}
